/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { gnoment } from 'shared-ui/gnoment';
import isEmpty from 'lodash/isEmpty';
import { Button, Counter, Icon, Tooltip } from 'sarsaparilla';
import { USE_BUTTON_INSTEAD_COUNTER } from 'ui-sitepass/src/constants';
import { durationLength } from '../utils/duration_utils';
import { getPrice } from '../utils';
import { PassTypeProps, ReservationProps } from '../props';
import { getValidPriceFromDivision } from '../utils/date_time_utils';

/**
 * NOTE: This component is used by ui-widget. We will need to check ui-widget whenever we do a change here
 */
export default class LEGACY_PassTypesTable extends PureComponent {
    static defaultProps = {
        pricing: null,
        passCounts: {},
        sortFn: (a, b) =>
            parseFloat(getPrice(b.periods).replace(/\/.*|\D/g, ' ')) -
            parseFloat(getPrice(a.periods).replace(/\/.*|\D/g, ' ')),
    };

    static propTypes = {
        maxTotalGuestsReached: PropTypes.bool.isRequired,
        onChange: PropTypes.func.isRequired,
        passCounts: PropTypes.object,
        passTypeId: PropTypes.string,
        passTypes: PropTypes.arrayOf(PassTypeProps).isRequired,
        passTypesRaw: PropTypes.arrayOf(PassTypeProps).isRequired,
        pricing: PropTypes.node,
        sortFn: PropTypes.func,
        facilityId: PropTypes.string,
        reservations: PropTypes.arrayOf(ReservationProps),
    };

    getPriceFromPeriods = (periods) => {
        const price = getValidPriceFromDivision(periods, gnoment());
        return isEmpty(price) ? '0' : price;
    };

    filterPassTypes = (passTypes) => {
        return passTypes.filter((item) => {
            const currentDate = gnoment().startOf('day');
            const hasValidStartDate = item.periods.some((period) =>
                currentDate.isSameOrAfter(gnoment(period.start_date).startOf('day'))
            );
            return hasValidStartDate;
        });
    };

    useButtonInsteadOfCounter = () => {
        return USE_BUTTON_INSTEAD_COUNTER.includes(this.props.facilityId);
    };

    renderRow = ({ id, name, periods, used_for: usedFor, duration }) => {
        const { maxTotalGuestsReached, passCounts, onChange } = this.props;
        const price = this.getPriceFromPeriods(periods);
        return (
            <tr
                key={`pass-type-${id}`}
                className={this.props.passTypeId === id ? 'highlight' : ''}
            >
                <td>
                    {name}
                    <Tooltip content={`${name} - ${usedFor}`}>
                        <Button
                            aria-label={name}
                            appearance="subtle"
                            size="small"
                            iconBeforeElement={<Icon iconName="info" size="xs" />}
                        />
                    </Tooltip>
                    <small className="mobile-only">{price}</small>
                </td>
                <td>{durationLength(duration)}</td>
                <td>{price}</td>
                <td>
                    {this.useButtonInsteadOfCounter() ? (
                        <Button
                            className="pass-types-table__content-item pass-types-table__button"
                            appearance="tertiary"
                            disabled={!isEmpty(this.props.reservations)}
                            onClick={() => {
                                onChange(id, 1);
                            }}
                        >
                            Add Pass
                        </Button>
                    ) : (
                        <div className="rec-guest-counter-wrap">
                            <div className="rec-guest-counter-row">
                                <Counter
                                    label={name}
                                    disableDecreaseCount={passCounts[id] <= 0}
                                    disableIncreaseCount={maxTotalGuestsReached}
                                    disableTextInput
                                    onCountChange={(value) => onChange(id, value)}
                                    count={passCounts[id]}
                                    iconClass="guest-counter-row-icon"
                                    inputClass="rec-guest-counter-row-quantity"
                                />
                            </div>
                        </div>
                    )}
                </td>
            </tr>
        );
    };

    onClick = (id) => {
        this.props.onChange(id, 1);
    };

    renderReducedRow = ({ id, name }) => {
        return (
            <tr key={`pass-type-${id}`}>
                <td>
                    {name}
                    <small className="mobile-only">Must be purchased on site</small>
                </td>
                <td>Must be purchased on site</td>
            </tr>
        );
    };

    render() {
        const { pricing, sortFn } = this.props;
        let { passTypes, passTypesRaw } = this.props;
        let hasPassTypes = !!passTypes?.length;
        let hasPassTypesRaw = !!passTypesRaw?.length;

        if (hasPassTypes) {
            passTypes = this.filterPassTypes(passTypes);
            hasPassTypes = !!passTypes?.length;
        }

        if (hasPassTypesRaw) {
            passTypesRaw = this.filterPassTypes(passTypesRaw);
            hasPassTypesRaw = !!passTypesRaw?.length;
        }

        if (!hasPassTypes && hasPassTypesRaw) {
            return (
                <div className="parkpass-pass-types-table-wrapper">
                    <table className="parkpass-pass-types-table">
                        <caption>
                            <span className="rec-sr-only">Site pass type</span>
                        </caption>
                        <thead>
                            <tr>
                                <th scope="col">Pass Type</th>
                                <th scope="col" />
                            </tr>
                        </thead>
                        <tbody>
                            {passTypesRaw.sort(sortFn).map(this.renderReducedRow)}
                        </tbody>
                    </table>
                    {pricing}
                </div>
            );
        }

        return (
            <div className="parkpass-pass-types-table-wrapper">
                <table className="parkpass-pass-types-table">
                    <caption>
                        <span className="rec-sr-only">Site pass selection</span>
                    </caption>
                    <thead>
                        <tr>
                            <th scope="col">Pass Type</th>
                            <th scope="col">Duration</th>
                            <th scope="col">Price</th>
                            {this.useButtonInsteadOfCounter() ? (
                                <th scope="col">Add</th>
                            ) : (
                                <th scope="col">Quantity</th>
                            )}
                        </tr>
                    </thead>
                    <tbody>{passTypes.sort(sortFn).map(this.renderRow)}</tbody>
                </table>
                {pricing}
            </div>
        );
    }
}

// cspell:ignore parkpass
